import './index.css';

import {
    CheckCircleTwoTone,
    CloseCircleOutlined,
    CloseCircleTwoTone,
    DownloadOutlined,
    DownOutlined,
    EditOutlined,
    RightOutlined,
    SettingOutlined,
    StopOutlined,
    SyncOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Divider,
    Input,
    message,
    Modal,
    Popover,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useManagementStore, useUserState } from '../../store';
import axios from '../../utilities/axios';
import { FORMAT } from '../../utilities/functions';
import AdminStatsPanel from './AdminStatsPanel';
import AdminGrossLiabilityChart from './AdminGrossLiabilityChart';
import AdminTradingAnalytics from './AdminTradingAnalytics';
import MarginRequirementsPanel from './MarginRequirementsPanel';
import getPayoutAlertBadges from './payoutAlerts';

const { Text } = Typography;
const NY_TZ = 'America/New_York';
const PAID_ACCOUNT_START_CUTOFF_LABEL = 'Oct 24, 2025';
const PAID_ACCOUNT_START_CUTOFF_START = dayjs('2025-10-24').startOf('day').valueOf();

const toNyEndOfDayISOString = (value) => {
    if (!value) return null;
    return dayjs.tz(value.format('YYYY-MM-DD'), 'YYYY-MM-DD', NY_TZ).endOf('day').toISOString();
};

const toUtcCalendarDate = (value) => {
    if (!value) return null;
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed : null;
};

const TYPE_OPTIONS = [
    { label: <span style={{ fontWeight: 500 }}>ALL</span>, value: 'ALL' },
    { label: <span style={{ fontWeight: 500 }}>Peak Capital Trading - PCT</span>, value: 'PCT' },
    { label: <span style={{ fontWeight: 500 }}>Bear Bull Traders - PTT</span>, value: 'PTT' },
    // { label: 'Trading Terminal - TTD', value: 'SIMULATOR_TTD' },
    {
        label: <span style={{ fontWeight: 500 }}>Trading Terminal - TTF</span>,
        value: 'SIMULATOR_TTF'
    }
];

const ADMIN_TYPE_META = {
    ALL: {
        shortLabel: 'All',
        titleLabel: 'All Accounts'
    },
    PCT: {
        shortLabel: 'PCT',
        titleLabel: 'Peak Capital Trading · PCT'
    },
    PTT: {
        shortLabel: 'PTT',
        titleLabel: 'Bear Bull Traders · PTT'
    },
    SIMULATOR_TTF: {
        shortLabel: 'TTF',
        titleLabel: 'Trading Terminal · TTF'
    }
};

// Workflow status options for dropdown
const WORKFLOW_STATUS_OPTIONS = [
    { label: <span style={{ fontWeight: 500 }}>Needs Review</span>, value: 'needs_review' },
    {
        label: <span style={{ fontWeight: 500 }}>Needs Review - Awaiting Info</span>,
        value: 'needs_review_awaiting_info'
    },
    {
        label: <span style={{ fontWeight: 500 }}>Reviewed - Contact</span>,
        value: 'reviewed_contact'
    },
    { label: <span style={{ fontWeight: 500 }}>Contacted</span>, value: 'reviewed_contacted' },
    {
        label: <span style={{ fontWeight: 500 }}>Responded - Awaiting Payment</span>,
        value: 'awaiting_payment'
    },
    {
        label: <span style={{ fontWeight: 500 }}>Reviewed - Not Eligible</span>,
        value: 'not_eligible'
    },
    { label: <span style={{ fontWeight: 500 }}>Paid</span>, value: 'paid' }
];

// Tab configuration
const TAB_ITEMS = [
    { key: 'needs_review_24h', label: 'Needs Review - Last 24h' },
    { key: 'needs_review', label: 'Needs Review' },
    { key: 'needs_review_awaiting_info', label: 'Needs Review - Awaiting Info' },
    { key: 'reviewed_contact', label: 'Reviewed - Contact' },
    { key: 'reviewed_contacted', label: 'Contacted' },
    { key: 'awaiting_payment', label: 'Responded - Awaiting Payment' },
    { key: 'paid', label: 'Paid' },
    { key: 'not_eligible', label: 'Reviewed - Not Eligible' },
    { key: 'open', label: 'Open' },
    { key: 'inactive', label: 'Inactive' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' }
];

const ACCOUNT_STATS_BREAKDOWN = [
    {
        key: 'closed_paid',
        label: 'Closed - Paid',
        subLabel: 'Paid',
        tabKeys: ['paid'],
        color: '#389e0d',
        hint: 'Closed accounts with payout completed.'
    },
    {
        key: 'closed_not_eligible',
        label: 'Closed - Not Eligible',
        subLabel: 'Not Eligible',
        tabKeys: ['not_eligible'],
        color: '#d4380d',
        hint: 'Closed accounts reviewed and marked ineligible for payout.'
    },
    {
        key: 'closed_archived_inactive',
        label: 'Closed - Archived & Inactive',
        subLabel: 'Archived & Inactive',
        tabKeys: ['archived', 'inactive'],
        color: '#64748b',
        hint: 'Archived or inactive accounts without an active payout workflow.'
    },
    {
        key: 'closed_under_review',
        label: 'Closed - Under Review',
        subLabel: 'Under Review',
        tabKeys: [
            'needs_review',
            'needs_review_awaiting_info',
            'reviewed_contact',
            'reviewed_contacted',
            'awaiting_payment'
        ],
        color: '#722ed1',
        hint: 'Closed accounts in the review and payout workflow.'
    }
];

const CLOSED_STATS_TAB_KEYS = [
    ...new Set(ACCOUNT_STATS_BREAKDOWN.flatMap((segment) => segment.tabKeys))
];

// Mapping between backend keys and frontend keys
const RISK_CONTROL_MAP = [
    {
        label: 'Max Loss ($)',
        value: 'max_loss',
        backendKey: 'maxLoss',
        hasInput: true,
        valueKey: 'value',
        enabledKey: 'enabled'
    },
    {
        label: 'Total Loss ($)',
        value: 'total_loss',
        backendKey: 'totalLoss',
        hasInput: true,
        valueKey: 'value',
        enabledKey: 'enabled'
    },
    {
        label: 'Equity Limit ($)',
        value: 'equity_limit',
        backendKey: 'equityLimit',
        hasInput: true,
        valueKey: 'value',
        enabledKey: 'enabled'
    },
    {
        label: 'Unwind All positions (EDT)',
        value: 'unwind_all',
        backendKey: 'unwindAllPositions',
        hasInput: true, // Now has input (date)
        valueKey: 'time',
        enabledKey: 'enabled'
    },
    {
        label: 'Prevent Premarket Trading',
        value: 'prevent_premarket',
        backendKey: 'preventPremarketTrading',
        hasInput: false
    },
    {
        label: 'Prevent After Market Trading',
        value: 'prevent_aftermarket',
        backendKey: 'preventAfterMarketTrading',
        hasInput: false
    }
];

const DEFAULT_VISIBLE_COLUMNS = {
    userName: false,
    type: false,
    fundedAccountFee: false
};

// Define available optional columns
const optionalColumns = [
    {
        title: 'User Name',
        dataIndex: ['user', 'name'],
        key: 'userName',
        render: (_, record) => record.user?.name || '-'
    },
    {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (type) => {
            if (type === 'simulator_ttf') return 'TTF';
            if (type === 'simulator_ttd') return 'TTD - Training Account';
            return type?.toUpperCase() || '-';
        }
    }
];

const optionalColumnToggles = [
    ...optionalColumns.map(({ title, key }) => ({ title, key })),
    { title: 'Funded Account Fee', key: 'fundedAccountFee', ttfOnly: true }
];

// Fetch all funded accounts at once
const fetchAccounts = (token) =>
    axios.get('crud/funded-accounts', { headers: { token } }).then(({ data }) => data);

const EMPTY_FUNDED_ACCOUNT_PURCHASE_STATS = {
    success: true,
    users: [],
    count: 0,
    withDiscount: 0
};

const fetchFundedAccountPurchaseStats = (token, emails = []) => {
    if (!emails.length) return Promise.resolve(EMPTY_FUNDED_ACCOUNT_PURCHASE_STATS);

    return axios
        .post(
            'report/paid-users',
            { emails },
            {
                headers: { token },
                timeout: 5 * 60 * 1000
            }
        )
        .then(({ data }) => data);
};

const fetchPayoutAlerts = (token) =>
    axios
        .get('crud/funded-accounts/payout-alerts', { headers: { token } })
        .then(({ data }) => data || {});

const getPaymentsForRecord = (report, record) => {
    if (!Array.isArray(report?.users) || !record?.user?.email) return [];

    const recordEmail = record.user.email.toLowerCase();
    return report.users.filter(
        (payment) => payment.customerEmail && payment.customerEmail.toLowerCase() === recordEmail
    );
};

const getLatestFundedAccountPayment = (report, record) => {
    const payments = getPaymentsForRecord(report, record);
    return payments.length > 0 ? payments[payments.length - 1] : null;
};

const MAX_PAYOUT_AMOUNT = 10000;

const getNumericValue = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;

    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (!trimmedValue) return null;

        const parsedValue = Number(trimmedValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    if (value && typeof value === 'object' && typeof value.toString === 'function') {
        const parsedValue = Number(value.toString());
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    return null;
};

const getAccountPnl = (account) => {
    const equity = getNumericValue(account?.equity);
    const initialFunds = getNumericValue(account?.initialFunds);

    if (equity === null || initialFunds === null) {
        return null;
    }

    return equity - initialFunds;
};

const getCappedPayoutAmount = (amount) => {
    const value = getNumericValue(amount);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.min(value, MAX_PAYOUT_AMOUNT);
};

const isPayoutEligible = (account) =>
    account?.riskViolation !== true && account?.workflowStatus !== 'not_eligible';

const isOpenAccount = (account) =>
    account?.isActive && !account?.archived && !account?.closedStatus && !account?.workflowStatus;

const isProfitableEligibleOpenAccount = (account) => {
    const pnl = getAccountPnl(account);
    return isOpenAccount(account) && Number.isFinite(pnl) && pnl > 0 && isPayoutEligible(account);
};

const isNotEligibleOpenAccount = (account) =>
    isOpenAccount(account) && !isProfitableEligibleOpenAccount(account);

const formatPercent = (numerator, denominator) =>
    denominator > 0 ? `${((numerator / denominator) * 100).toFixed(1)}%` : '0%';

const matchesTabFilter = (acc, tabKey) => {
    if (tabKey === 'all') return true;

    if (tabKey === 'open') {
        return !acc.archived && acc.isActive && !acc.closedStatus && !acc.workflowStatus;
    }

    if (tabKey === 'inactive') {
        return !acc.archived && !acc.isActive && !acc.closedStatus && !acc.workflowStatus;
    }

    if (tabKey === 'needs_review') {
        return !acc.isActive && acc.workflowStatus === 'needs_review';
    }

    if (tabKey === 'needs_review_24h') {
        return (
            !acc.isActive &&
            acc.workflowStatus === 'needs_review' &&
            acc.closedAt &&
            dayjs(acc.closedAt).isAfter(dayjs().subtract(24, 'hour'))
        );
    }

    if (tabKey === 'needs_review_awaiting_info') {
        return !acc.isActive && acc.workflowStatus === 'needs_review_awaiting_info';
    }

    if (tabKey === 'not_eligible') {
        return !acc.isActive && acc.workflowStatus === 'not_eligible';
    }

    if (tabKey === 'reviewed_contact') {
        return !acc.isActive && acc.workflowStatus === 'reviewed_contact';
    }

    if (tabKey === 'reviewed_contacted') {
        return !acc.isActive && acc.workflowStatus === 'reviewed_contacted';
    }

    if (tabKey === 'awaiting_payment') {
        return !acc.isActive && acc.workflowStatus === 'awaiting_payment';
    }

    if (tabKey === 'paid') {
        return !acc.isActive && acc.workflowStatus === 'paid';
    }

    if (tabKey === 'archived') {
        return acc.archived === true && !acc.workflowStatus;
    }

    return true;
};

const countAccountsForTabKeys = (accounts, tabKeys) =>
    accounts.filter((account) => tabKeys.some((tabKey) => matchesTabFilter(account, tabKey)))
        .length;

const isPaidWorkflowAccount = (account) => matchesTabFilter(account, 'paid');

const isOpenWorkflowAccount = (account) => matchesTabFilter(account, 'open');

const isClosedStatsAccount = (account) =>
    CLOSED_STATS_TAB_KEYS.some((tabKey) => matchesTabFilter(account, tabKey));

const PENDING_PAYOUT_WORKFLOW_STATUSES = [
    'needs_review',
    'needs_review_awaiting_info',
    'reviewed_contact',
    'reviewed_contacted',
    'awaiting_payment'
];

const isPendingPayoutWorkflowAccount = (account) =>
    PENDING_PAYOUT_WORKFLOW_STATUSES.includes(account?.workflowStatus);

const getLivePayoutExposureAmount = (account) =>
    isOpenAccount(account) && isPayoutEligible(account)
        ? getCappedPayoutAmount(getAccountPnl(account))
        : 0;

const getPaidPayoutAmount = (account) => getCappedPayoutAmount(account?.closedBalance);

const isArchivedStatsAccount = (account) => account?.archived === true && !account?.workflowStatus;

const getEligiblePayoutExposureFromBalances = (account) => {
    if (!isPayoutEligible(account)) {
        return 0;
    }

    const closedBalance = getNumericValue(account?.closedBalance);
    if (Number.isFinite(closedBalance)) {
        return getCappedPayoutAmount(closedBalance);
    }

    const pnl = getAccountPnl(account);
    if (Number.isFinite(pnl)) {
        return getCappedPayoutAmount(pnl);
    }

    return 0;
};

const getClosedPayoutExposureAmount = (account) => {
    if (!isPendingPayoutWorkflowAccount(account)) {
        return 0;
    }

    return getEligiblePayoutExposureFromBalances(account);
};

const getPayoutExposureAmount = (account) => {
    if (isPaidWorkflowAccount(account)) {
        return getPaidPayoutAmount(account);
    }

    if (isOpenAccount(account)) {
        return getLivePayoutExposureAmount(account);
    }

    if (isArchivedStatsAccount(account)) {
        return getEligiblePayoutExposureFromBalances(account);
    }

    return getClosedPayoutExposureAmount(account);
};

const hasPayoutExposureData = (account) => {
    if (isPaidWorkflowAccount(account)) {
        return getNumericValue(account?.closedBalance) !== null;
    }

    if (isOpenAccount(account)) {
        return getAccountPnl(account) !== null;
    }

    if (isPendingPayoutWorkflowAccount(account) || isArchivedStatsAccount(account)) {
        return getNumericValue(account?.closedBalance) !== null || getAccountPnl(account) !== null;
    }

    return getAccountPnl(account) !== null || getNumericValue(account?.closedBalance) !== null;
};

const getAccountEndDate = (account) =>
    account?.accountEndDate || account?.expirationDate || account?.account_end_date;

const getAccountStartDate = (account) =>
    account?.activationDate || account?.accountStartDate || account?.createdAt;

const getAccountActivationDate = (account) => account?.activationDate || account?.accountStartDate;

const getAccountAgeDays = (account) => {
    const activationDate = getAccountActivationDate(account);
    if (!activationDate) return null;

    const activationDay = toUtcCalendarDate(activationDate)?.startOf('day');
    if (!activationDay?.isValid()) return null;

    const today = dayjs.utc().startOf('day');
    return Math.max(0, Math.floor(today.diff(activationDay, 'day', true)));
};

const PAYOUT_ALERT_MIN_ACCOUNT_AGE_DAYS = 90;

const PAYOUT_ALERT_REVIEW_CLOSED_TABS = new Set([
    'all',
    'needs_review_24h',
    'needs_review',
    'needs_review_awaiting_info',
    'reviewed_contact',
    'reviewed_contacted',
    'awaiting_payment',
    'not_eligible',
    'paid'
]);

const isPayoutAlertReviewClosedAccount = (account) => {
    if (isOpenAccount(account)) return false;
    if (matchesTabFilter(account, 'inactive')) return false;
    if (matchesTabFilter(account, 'archived')) return false;
    return Boolean(account?.workflowStatus) || Boolean(account?.closedStatus);
};

const shouldShowBelowMinTradingDaysAlert = (account) => {
    if (!isPayoutAlertReviewClosedAccount(account)) return false;
    const ageDays = getAccountAgeDays(account);
    return ageDays != null && ageDays >= PAYOUT_ALERT_MIN_ACCOUNT_AGE_DAYS;
};

const getPayoutAlertColumnFilters = (activeTab) => {
    const filters = [
        { text: '>30% PnL', value: 'tradeOver30PctTotalPnL' },
        { text: '<2 Trades/10d', value: 'lowRolling10DayAvg' },
        { text: 'Below $5', value: 'stocksBelow5' }
    ];

    if (PAYOUT_ALERT_REVIEW_CLOSED_TABS.has(activeTab)) {
        filters.push({ text: '<36 Days (90d+)', value: 'belowMinTradingDays' });
    }

    filters.push({ text: 'No alerts', value: 'none' });
    return filters;
};

const accountHasVisiblePayoutAlerts = (record, alertsByAccount) => {
    const showProfile = ['simulator_ttf', 'pct', 'ptt'].includes(record.type);
    if (!showProfile) return false;

    const alerts = alertsByAccount[record.accountUniqueId];
    const badges = getPayoutAlertBadges(alerts, {
        showBelowMinTradingDays: shouldShowBelowMinTradingDaysAlert(record)
    });
    return badges.length > 0;
};

const matchesPayoutAlertColumnFilter = (record, filterValue, alertsByAccount) => {
    const showProfile = ['simulator_ttf', 'pct', 'ptt'].includes(record.type);
    const alerts = showProfile ? alertsByAccount[record.accountUniqueId] : null;

    if (filterValue === 'none') {
        return !accountHasVisiblePayoutAlerts(record, alertsByAccount);
    }

    if (!showProfile || !alerts) return false;
    if (filterValue === 'tradeOver30PctTotalPnL') return Boolean(alerts.tradeOver30PctTotalPnL);
    if (filterValue === 'lowRolling10DayAvg') return Boolean(alerts.lowRolling10DayAvg);
    if (filterValue === 'stocksBelow5') {
        return Array.isArray(alerts.stocksBelow5) && alerts.stocksBelow5.length > 0;
    }
    if (filterValue === 'belowMinTradingDays') {
        return shouldShowBelowMinTradingDaysAlert(record) && Boolean(alerts.belowMinTradingDays);
    }
    return true;
};

const PAYOUT_EXPOSURE_COLUMN_FILTERS = [
    { text: 'Positive', value: 'positive' },
    { text: 'Zero', value: 'zero' },
    { text: 'Negative', value: 'negative' }
];

const getPayoutExposureFilterValue = (record) => {
    if (!hasPayoutExposureData(record)) return null;
    const amount = getPayoutExposureAmount(record);
    if (amount > 0) return 'positive';
    if (amount < 0) return 'negative';
    return 'zero';
};

const ACCOUNT_AGE_COLUMN_FILTERS = [
    { text: 'New (< 5 days)', value: 'new' },
    { text: 'Old (85+ days)', value: 'old' }
];

const matchesAccountAgeColumnFilter = (record, filterValue) => {
    const ageDays = getAccountAgeDays(record);
    if (ageDays == null) return false;
    if (filterValue === 'new') return ageDays < 5;
    if (filterValue === 'old') return ageDays >= 85;
    return true;
};

const isProgramCompleter = (account) => {
    if (isOpenWorkflowAccount(account)) return false;

    // Paid accounts completed review; date edge cases should not exclude them
    if (isPaidWorkflowAccount(account)) return true;

    if (!isClosedStatsAccount(account)) return false;

    const end = getAccountEndDate(account);
    if (!end) return false;

    // Program ended: expiration is today or earlier (still in-program accounts excluded)
    return dayjs(end).endOf('day').valueOf() <= dayjs().endOf('day').valueOf();
};

// Update account workflow status
const updateAccountStatus = async (token, accountId, workflowStatus) => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/update-workflow-status/${accountId}`,
        { workflowStatus },
        { headers: { token } }
    );
    return data;
};

const activateFundedAccount = async (params) => {
    const { token, accountId, reinstateMode = 'mark_out' } = params;
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/activate-funded-account`,
        { accountId, reinstateMode },
        { headers: { token } }
    );
    return data;
};

const archiveFundedAccount = async (params) => {
    const { token, accountId } = params;
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/archive-funded-account/${accountId}`,
        {},
        { headers: { token } }
    );
    return data;
};

const updateEndDate = async (token, accountId, expirationDate) => {
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/update-end-date/${accountId}`,
        { expirationDate },
        { headers: { token } }
    );
    return data;
};

const updateDrawdownCheckStatus = async (params) => {
    const { token, accountId, drawdownCheckStatus } = params;
    const { data } = await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/update-drawdown-check-status/${accountId}`,
        { drawdownCheckStatus },
        { headers: { token } }
    );
    return data;
};

// Close all positions for all accounts
const closeAllPositions = async () => {
    await axios.put(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/order/remove-all-at-close`,
        { isFromAPIServer: true, time: dayjs().tz().toDate() },
        { headers: { authorization: `Bearer ${process.env.BYPASS_TOKEN}` } }
    );
};

// GET /global-risk-config
const fetchGlobalRiskConfig = (token) =>
    axios
        .get(`${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/global-risk-config`, {
            headers: { token }
        })
        .then(({ data }) => data);

// POST /admin/apply-riskconfig-to-all
const applyGlobalRiskConfig = async (params) => {
    // params: { token, riskConfig, removeOverrides }
    const { token, riskConfig, removeOverrides: removeOverridesParam } = params;
    const { data } = await axios.post(
        `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/apply-riskconfig-to-all`,
        { riskConfig, removeOverrides: removeOverridesParam },
        { headers: { token } }
    );
    return data;
};

export default function AdminDashboard() {
    const { getUser } = useUserState();
    const { setViewAs, setCompany, adminPreferences, setAdminSelectedType } = useManagementStore(
        (state) => state
    );
    const user = getUser();
    const navigate = useNavigate();
    const selectedType = adminPreferences?.selectedFundedType || 'SIMULATOR_TTF';
    const lastViewedFundedAccountId = adminPreferences?.lastFundedAccountId || null;
    const selectedTypeMeta = ADMIN_TYPE_META[selectedType] || ADMIN_TYPE_META.ALL;

    // Filters and search
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [tableFilteredCount, setTableFilteredCount] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkWorkflowStatus, setBulkWorkflowStatus] = useState();
    const [bulkEndDateValue, setBulkEndDateValue] = useState(null);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);

    // Close positions popover state
    const [closePositionsPopoverOpen, setClosePositionsPopoverOpen] = useState(false);

    // Active tab state
    const [activeTab, setActiveTab] = useState('needs_review');

    // Global settings
    const [globalSettingsOpen, setGlobalSettingsOpen] = useState(false);
    const [globalRiskControls, setGlobalRiskControls] = useState([]);
    const [globalRiskControlValues, setGlobalRiskControlValues] = useState({
        max_loss: '',
        total_loss: '',
        equity_limit: '',
        unwind_all: '', // For unwind_all, this will be a date string (ISO)
        prevent_premarket: '',
        prevent_aftermarket: ''
    });
    // Add removeOverrides state, default to false
    const [removeOverrides, setRemoveOverrides] = useState(false);

    // CSV download spinner state
    const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
    const [usePaidAccountStatsScope, setUsePaidAccountStatsScope] = useState(true);
    const [grossLiabilityChartOpen, setGrossLiabilityChartOpen] = useState(false);
    const [statsCollapsed, setStatsCollapsed] = useState(() => {
        const saved = localStorage.getItem('admin-stats-collapsed');
        return saved === 'true';
    });

    // Payout eligibility modal state
    const [eligibilityModalOpen, setEligibilityModalOpen] = useState(false);
    const [eligibilityModalData, setEligibilityModalData] = useState({
        record: null,
        nextValue: null,
        existingReasons: null
    });
    const [selectedReasons, setSelectedReasons] = useState([]);

    // Column visibility state - initialize with localStorage values
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const savedColumns = localStorage.getItem('admin-table-columns');
        if (savedColumns) {
            try {
                return { ...DEFAULT_VISIBLE_COLUMNS, ...JSON.parse(savedColumns) };
            } catch (error) {
                console.error('Failed to parse saved column preferences:', error);
            }
        }
        return { ...DEFAULT_VISIBLE_COLUMNS };
    });

    // Fetch all funded accounts once
    const {
        data: fundedAccountsData,
        isLoading,
        refetch
    } = useQuery(['funded-accounts'], () => fetchAccounts(user?.token));

    const { data: payoutAlertsByAccount = {} } = useQuery(
        ['funded-accounts-payout-alerts'],
        () => fetchPayoutAlerts(user?.token),
        {
            enabled: !!user?.token,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false
        }
    );

    const isTtfStatsType = selectedType === 'SIMULATOR_TTF';

    const statsAccounts = useMemo(() => {
        if (!Array.isArray(fundedAccountsData)) return [];

        const typeScopedAccounts = fundedAccountsData.filter(
            (account) =>
                selectedType === 'ALL' || (account.type || '').toUpperCase() === selectedType
        );

        if (!isTtfStatsType || !usePaidAccountStatsScope) return typeScopedAccounts;

        return typeScopedAccounts.filter((account) => {
            const accountStartDate = getAccountStartDate(account);
            if (!accountStartDate) return false;

            const startTime = dayjs(accountStartDate).valueOf();
            return Number.isFinite(startTime) && startTime >= PAID_ACCOUNT_START_CUTOFF_START;
        });
    }, [fundedAccountsData, isTtfStatsType, selectedType, usePaidAccountStatsScope]);

    const fundedAccountEmails = useMemo(() => {
        if (!Array.isArray(fundedAccountsData)) return [];

        return [
            ...new Set(
                fundedAccountsData
                    .filter((account) => (account?.type || '').toUpperCase() === 'SIMULATOR_TTF')
                    .map((account) => account?.user?.email)
                    .filter(Boolean)
                    .map((email) => email.toLowerCase().trim())
            )
        ];
    }, [fundedAccountsData]);

    const {
        data: fundedAccountPurchaseStats = EMPTY_FUNDED_ACCOUNT_PURCHASE_STATS,
        isLoading: isLoadingFundedAccountPurchaseStats,
        isError: isFundedAccountPurchaseStatsError
    } = useQuery(
        ['funded-account-purchase-stats', fundedAccountEmails],
        () => fetchFundedAccountPurchaseStats(user?.token, fundedAccountEmails),
        {
            enabled:
                Boolean(user?.token) &&
                selectedType === 'SIMULATOR_TTF' &&
                visibleColumns.fundedAccountFee &&
                fundedAccountEmails.length > 0
        }
    );

    const canResumeLastViewedAccount = useMemo(
        () =>
            Boolean(lastViewedFundedAccountId) &&
            Array.isArray(fundedAccountsData) &&
            fundedAccountsData.some(
                (account) => account.accountUniqueId === lastViewedFundedAccountId
            ),
        [fundedAccountsData, lastViewedFundedAccountId]
    );

    const updateRiskViolation = async (params) => {
        const { token, accountId, value, reason } = params;
        const { data } = await axios.put(
            `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/mark-risk-violation/${accountId}`,
            { value, reasons: reason },
            { headers: { token } }
        );
        return data;
    };

    const { mutate: mutateRiskViolation, isLoading: isUpdatingRisk } = useMutation({
        mutationFn: (params) => updateRiskViolation(params),
        onSuccess: (data) => {
            message.success(data?.message || 'Updated payout eligibility');
            refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update payout eligibility');
        }
    });

    // Mutation for updating account status
    const { mutate: mutateAccountStatus, isLoading: isUpdatingStatus } = useMutation({
        mutationFn: (params) =>
            updateAccountStatus(params.token, params.accountId, params.workflowStatus),
        onSuccess: () => {
            message.success('Account status updated successfully');
            refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update account status');
        }
    });

    const { mutate: mutateActivateAccount, isLoading: isUpdatingActivation } = useMutation({
        mutationFn: (params) => activateFundedAccount(params),
        onSuccess: (_, variables) => {
            message.success(
                variables?.reinstateMode === 'reset'
                    ? 'TRPTT account reset and reactivated'
                    : 'Account marked OUT and reactivated'
            );
            refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to reactivate account');
        }
    });

    const { mutate: mutateArchiveAccount } = useMutation({
        mutationFn: (params) => archiveFundedAccount(params),
        onSuccess: () => {
            message.success('Account archived successfully');
            refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to archive account');
        }
    });

    const { mutate: mutateDrawdownCheckStatus, isLoading: isUpdatingDrawdownStatus } = useMutation({
        mutationFn: (params) => updateDrawdownCheckStatus(params),
        onSuccess: (data) => {
            message.success(data?.message || 'Drawdown check status updated');
            refetch();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to update OUT status');
        }
    });

    const confirmChangePayoutEligibility = (record, nextValue, existingReasons = null) => {
        // If existingReasons not provided, try to get them from the record
        let reasonsToLoad = existingReasons;
        if (!reasonsToLoad && record) {
            reasonsToLoad = record?.riskViolationReason || null;
        }

        const initialReasons =
            reasonsToLoad && Array.isArray(reasonsToLoad) ? [...reasonsToLoad] : [];
        setEligibilityModalData({ record, nextValue, existingReasons: reasonsToLoad });
        setSelectedReasons(initialReasons);
        setEligibilityModalOpen(true);
    };

    const handleEligibilityModalOk = () => {
        const { record, nextValue } = eligibilityModalData;
        const eligible = nextValue === false; // false => eligible

        if (!eligible && (!selectedReasons || selectedReasons.length === 0)) {
            message.error('Please select at least one reason');
            return;
        }

        mutateRiskViolation({
            token: user?.token,
            accountId: record._id || record.id || record.accountId || record.key,
            value: nextValue,
            reason: selectedReasons && selectedReasons.length > 0 ? selectedReasons : undefined
        });

        setEligibilityModalOpen(false);
    };

    const handleEligibilityModalCancel = () => {
        setEligibilityModalOpen(false);
        setSelectedReasons([]);
    };

    const REASON_OPTIONS = [
        { value: 'equityBelow9500', label: 'Equity Below $9,500' },
        { value: 'equityBelow10500', label: 'Equity Below $10,500' },
        { value: 'maxResets3', label: 'Maximum Resets (3)' },
        { value: 'didNotMeet36Days', label: 'Minimum Trading Days (36)' },
        { value: 'didNotMeet10Day2TradesRollingAvg', label: '10-Day Rolling Avg of 2 Trades' },
        { value: 'stocksBelow5', label: 'Traded Stocks Below $5' },
        { value: 'tradeOver30PctTotalPnL', label: 'Trade > 30% Total PnL' },
        { value: 'leveragedETF', label: 'Leveraged ETF' },
        { value: 'other', label: 'Other' }
    ];

    const REASON_SHORT_LABELS = {
        equityBelow9500: '$9.5K Hit',
        equityBelow10500: '$10.5K Hit',
        maxResets3: '3+ Resets',
        didNotMeet36Days: '<36 Days',
        didNotMeet10Day2TradesRollingAvg: '<2 Trades/10-Day',
        stocksBelow5: 'Below $5',
        tradeOver30PctTotalPnL: '>30% Total PnL',
        leveragedETF: 'Leveraged',
        other: 'Other'
    };

    const eligible = eligibilityModalData.nextValue === false; // false => eligible
    let modalTitle;
    if (eligible) {
        modalTitle = 'Mark Eligible for Payout?';
    } else if (eligibilityModalData.existingReasons) {
        modalTitle = 'Edit Risk Violation Reasons';
    } else {
        modalTitle = 'Mark NOT Eligible (Risk Violation)?';
    }

    // Fetch latest global risk config
    const {
        data: globalRiskConfigData,
        isLoading: isLoadingGlobalRiskConfig,
        refetch: refetchGlobalRiskConfig
    } = useQuery(['global-risk-config'], () => fetchGlobalRiskConfig(user?.token), {
        enabled: !!user?.token,
        // Don't retry on 404 (not found)
        retry: (failureCount, error) => {
            if (error?.response?.status === 404) return false;
            return failureCount < 3;
        }
    });

    // Save column preferences to localStorage when they change
    useEffect(() => {
        localStorage.setItem('admin-table-columns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    useEffect(() => {
        localStorage.setItem('admin-stats-collapsed', String(statsCollapsed));
    }, [statsCollapsed]);

    // Consolidated status logic

    // When globalSettingsOpen is opened, populate controls from backend config
    useEffect(() => {
        if (globalSettingsOpen && globalRiskConfigData) {
            // Determine which controls are enabled in config
            const enabledControls = [];
            const values = {
                max_loss: '',
                total_loss: '',
                equity_limit: '',
                unwind_all: '',
                prevent_premarket: '',
                prevent_aftermarket: ''
            };

            RISK_CONTROL_MAP.forEach((opt) => {
                const backendVal = globalRiskConfigData[opt.backendKey];
                if (opt.hasInput) {
                    // Controls with input (object with enabled/value or enabled/date)
                    if (
                        backendVal &&
                        typeof backendVal === 'object' &&
                        backendVal[opt.enabledKey]
                    ) {
                        enabledControls.push(opt.value);
                        if (opt.value === 'unwind_all') {
                            // For unwind_all, value is a date string
                            values[opt.value] =
                                backendVal[opt.valueKey] !== undefined &&
                                backendVal[opt.valueKey] !== null
                                    ? backendVal[opt.valueKey]
                                    : '';
                        } else {
                            values[opt.value] =
                                backendVal[opt.valueKey] !== undefined &&
                                backendVal[opt.valueKey] !== null
                                    ? backendVal[opt.valueKey]
                                    : '';
                        }
                    }
                } else if (typeof backendVal === 'boolean' && backendVal) {
                    // Controls without input
                    enabledControls.push(opt.value);
                    values[opt.value] = true;
                }
            });

            setGlobalRiskControls(enabledControls);
            setGlobalRiskControlValues(values);
        }
        // If config not found (404), reset
        if (globalSettingsOpen && globalRiskConfigData === undefined) {
            setGlobalRiskControls([]);
            setGlobalRiskControlValues({
                max_loss: '',
                total_loss: '',
                equity_limit: '',
                unwind_all: '',
                prevent_premarket: '',
                prevent_aftermarket: ''
            });
        }
        // Reset removeOverrides to false when opening
        if (globalSettingsOpen) {
            setRemoveOverrides(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Rehydrate global settings only when the modal opens or fetched settings change.
    }, [globalSettingsOpen, globalRiskConfigData]);

    // Mutation for applying global risk config
    const { mutate: mutateApplyRiskConfig, isLoading: isApplyingRiskConfig } = useMutation({
        mutationFn: (params) => applyGlobalRiskConfig(params),
        onSuccess: (data) => {
            message.success(data?.message || 'Global risk controls saved');
            setGlobalSettingsOpen(false);
            refetch();
            refetchGlobalRiskConfig();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to apply global risk controls');
        }
    });

    // Get workflow status from record (from backend)
    const getWorkflowStatus = (record) => record.workflowStatus || null;

    // Handle status change
    const handleStatusChange = (record, newStatus) => {
        const accountId = record._id || record.id || record.accountId || record.key;

        mutateAccountStatus({
            token: user?.token,
            accountId,
            workflowStatus: newStatus
        });
    };

    // Consolidated status logic
    // Returns { text, color, tagColor }
    const getConsolidatedStatus = (record) => {
        // Add an explicit check for archived status at the top
        if (record.archived) {
            return { text: 'Archived', color: '#000000', tagColor: 'black' };
        }

        // If active and not closed, it's Open
        if (record.isActive) {
            // ... (the rest of the function remains the same)
            if (!record.closedStatus) {
                return { text: 'Open', color: '#2962FF', tagColor: 'blue' };
            }
            return { text: 'Open', color: '#2962FF', tagColor: 'blue' };
        }
        // Inactive
        if (!record.closedStatus && record.isActive === false) {
            return { text: 'Inactive', color: '#d4380d', tagColor: 'red' };
        }
        // Closed with status
        if (record.closedStatus === 'positive' && record.closedReviewed === false) {
            return {
                text: 'Needs Review',
                color: '#389e0d',
                tagColor: 'green'
            };
        }
        // Closed with status
        if (record.closedStatus === 'negative' && record.closedReviewed === false) {
            return {
                text: 'No Review Needed',
                color: '#d4380d',
                tagColor: 'red'
            };
        }
        // If closedStatus is positive/negative and closedReviewed is true, it's Paid
        if (record.closedStatus === 'paid' && record.closedReviewed === true) {
            return {
                text: 'Paid',
                color: '#389e0d',
                tagColor: 'green'
            };
        }
        // Fallback for any other unknown cases
        return { text: 'Unknown', color: '#8c8c8c', tagColor: 'default' };
    };

    // Filtered accounts (by type, search, and tab, in frontend)
    const filteredAccounts = useMemo(() => {
        if (!fundedAccountsData) return [];

        return fundedAccountsData.filter((acc) => {
            const matchesType =
                selectedType === 'ALL' || (acc.type || '').toUpperCase() === selectedType;
            const s = search.toLowerCase();

            const statusObj = getConsolidatedStatus(acc);
            const statusText = (statusObj?.text || '').toLowerCase();

            const matchesSearch =
                acc.accountUniqueId?.toLowerCase().includes(s) ||
                acc.user?.name?.toLowerCase().includes(s) ||
                acc.user?.email?.toLowerCase().includes(s) ||
                acc.type?.toLowerCase().includes(s) ||
                (statusText && statusText.includes(s));

            const matchesTab = matchesTabFilter(acc, activeTab);

            return matchesType && matchesSearch && matchesTab;
        });
    }, [fundedAccountsData, selectedType, search, activeTab]);

    const typeScopedAccounts = useMemo(() => {
        if (!Array.isArray(fundedAccountsData)) return [];

        return fundedAccountsData.filter(
            (acc) => selectedType === 'ALL' || (acc.type || '').toUpperCase() === selectedType
        );
    }, [fundedAccountsData, selectedType]);

    const tabItemsWithCounts = useMemo(
        () =>
            TAB_ITEMS.map(({ key, label }) => {
                const count = typeScopedAccounts.filter((acc) => matchesTabFilter(acc, key)).length;
                return {
                    key,
                    label: `${label} (${count})`
                };
            }),
        [typeScopedAccounts]
    );

    useEffect(() => {
        setTableFilteredCount(filteredAccounts.length);
    }, [filteredAccounts]);

    const tableRowCount = tableFilteredCount ?? filteredAccounts.length;

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(tableRowCount / pageSize) || 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [tableRowCount, pageSize, page]);

    const selectedAccounts = useMemo(() => {
        if (!fundedAccountsData || selectedRowKeys.length === 0) return [];
        const selectedIds = new Set(selectedRowKeys);
        return fundedAccountsData.filter((acc) => selectedIds.has(acc._id));
    }, [fundedAccountsData, selectedRowKeys]);

    const selectedReinstateAccounts = useMemo(
        () =>
            selectedAccounts.filter(
                (acc) => ['pct', 'ptt'].includes(acc.type) && !acc.isActive && !acc.archived
            ),
        [selectedAccounts]
    );

    const selectedArchivableAccounts = selectedReinstateAccounts;

    // Table pagination config
    const pagination = useMemo(
        () => ({
            current: page,
            pageSize,
            total: tableRowCount,
            onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
            },
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100', '500', '1000'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
        }),
        [page, pageSize, tableRowCount]
    );

    // --- STATISTICS CALCULATIONS (adjusted for new status logic) ---
    const statsScopeLabel =
        isTtfStatsType && usePaidAccountStatsScope
            ? `${selectedTypeMeta.shortLabel} accounts started on or after ${PAID_ACCOUNT_START_CUTOFF_LABEL}`
            : `All ${selectedTypeMeta.shortLabel} accounts`;
    const totalAccounts = statsAccounts.length;
    const openAccounts = statsAccounts.filter(isOpenAccount);

    const totalOpenAccounts = openAccounts.length;

    const paidWorkflowAccounts = statsAccounts.filter(isPaidWorkflowAccount);
    const paidAccountsCount = paidWorkflowAccounts.length;
    const closedAccountsCount = statsAccounts.filter(isClosedStatsAccount).length;

    // Eligible profitable open = open, profitable, and riskViolation !== true
    const numEligibleProfitableOpen = openAccounts.filter(isProfitableEligibleOpenAccount).length;
    const numNotEligibleOpen = openAccounts.filter(isNotEligibleOpenAccount).length;
    const totalOpenEligibleAccounts = openAccounts.filter(isPayoutEligible).length;

    const programCompleterAccounts = statsAccounts.filter(isProgramCompleter);
    const programCompleterClosedCount = programCompleterAccounts.length;
    const programCompleterPaidCount = programCompleterAccounts.filter(isPaidWorkflowAccount).length;

    const programSurvivalRate = formatPercent(
        programCompleterPaidCount,
        programCompleterClosedCount
    );
    const closedSurvivalRate = formatPercent(paidAccountsCount, closedAccountsCount);
    const openProfitableOfAllOpenPercent = formatPercent(
        numEligibleProfitableOpen,
        totalOpenAccounts
    );
    const openProfitableOfEligibleOpenPercent = formatPercent(
        numEligibleProfitableOpen,
        totalOpenEligibleAccounts
    );

    const totalPayoutPaid = paidWorkflowAccounts.reduce(
        (sum, acc) => sum + getPaidPayoutAmount(acc),
        0
    );

    const totalExpectedPayoutOpen = openAccounts.reduce(
        (sum, acc) => sum + getLivePayoutExposureAmount(acc),
        0
    );

    const eligibleOpenAccounts = openAccounts.filter(isPayoutEligible);
    const avgPayoutClosed = closedAccountsCount > 0 ? totalPayoutPaid / closedAccountsCount : 0;
    const avgPayoutPaid = paidAccountsCount > 0 ? totalPayoutPaid / paidAccountsCount : 0;
    const avgPayoutOpen =
        openAccounts.length > 0 ? totalExpectedPayoutOpen / openAccounts.length : 0;

    const next30Cutoff = dayjs().add(30, 'day').endOf('day').valueOf();
    const next30EndingAccounts = eligibleOpenAccounts.filter((acc) => {
        const accountEndDate = getAccountEndDate(acc);
        if (!accountEndDate) return false;

        const endDate = dayjs(accountEndDate);
        const endTime = endDate.valueOf();
        return Number.isFinite(endTime) && endTime <= next30Cutoff;
    });
    const next30EndingAccountsCount = next30EndingAccounts.length;
    const next30PayoutExposure = next30EndingAccounts.reduce(
        (sum, acc) => sum + getLivePayoutExposureAmount(acc),
        0
    );
    const paidLast90Start = dayjs().subtract(90, 'day').startOf('day').valueOf();
    const paidLast90End = dayjs().endOf('day').valueOf();
    const totalPayoutPaidLast90Days = paidWorkflowAccounts
        .filter((acc) => {
            if (!acc?.closedPaidAt) return false;

            const paidDate = dayjs(acc.closedPaidAt);
            const paidTime = paidDate.valueOf();
            return (
                Number.isFinite(paidTime) &&
                paidTime >= paidLast90Start &&
                paidTime <= paidLast90End
            );
        })
        .reduce((sum, acc) => sum + getPaidPayoutAmount(acc), 0);

    const statsPanelData = useMemo(() => {
        const accountBreakdownClosedRows = ACCOUNT_STATS_BREAKDOWN.map((segment) => ({
            ...segment,
            count: countAccountsForTabKeys(statsAccounts, segment.tabKeys)
        }));

        const accountBreakdownOpenRows = [
            {
                key: 'open_profitable_eligible',
                label: 'Open - Profitable and Eligible',
                subLabel: 'Profitable and Eligible',
                count: numEligibleProfitableOpen,
                color: '#2962FF',
                hint: 'Open, positive P/L, and payout eligible.'
            },
            {
                key: 'open_not_eligible',
                label: 'Open - Not Eligible',
                subLabel: 'Not Eligible',
                count: numNotEligibleOpen,
                color: '#94a3b8',
                hint: 'Open accounts that are unprofitable or not payout eligible.'
            }
        ];

        const accountBreakdownOpenTotal = accountBreakdownOpenRows.reduce(
            (sum, row) => sum + row.count,
            0
        );
        const accountBreakdownClosedTotal = countAccountsForTabKeys(
            statsAccounts,
            CLOSED_STATS_TAB_KEYS
        );

        const barSegments = [...accountBreakdownOpenRows, ...accountBreakdownClosedRows].map(
            ({ key, label, color, count }) => ({
                key,
                label,
                color,
                count
            })
        );

        const categorizedCount = accountBreakdownOpenTotal + accountBreakdownClosedTotal;

        return {
            barSegments,
            accountBreakdownOpenRows,
            accountBreakdownOpenTotal,
            accountBreakdownClosedRows,
            accountBreakdownClosedTotal,
            uncategorizedCount: Math.max(0, statsAccounts.length - categorizedCount),
            profitability: {
                openTotal: totalOpenAccounts,
                openEligibleTotal: totalOpenEligibleAccounts,
                openProfitableEligible: numEligibleProfitableOpen,
                openProfitableOfAllOpenPercent,
                openProfitableOfEligibleOpenPercent,
                paidTotal: paidAccountsCount,
                closedTotal: closedAccountsCount,
                closedSurvivalRate,
                programPaidTotal: programCompleterPaidCount,
                programClosedTotal: programCompleterClosedCount,
                programSurvivalRate
            },
            payouts: {
                totalPaid: FORMAT.NUMBER_TO_CURRENCY(totalPayoutPaid),
                paidLast90Days: FORMAT.NUMBER_TO_CURRENCY(totalPayoutPaidLast90Days),
                grossLiability: FORMAT.NUMBER_TO_CURRENCY(totalExpectedPayoutOpen),
                avgClosed: FORMAT.NUMBER_TO_CURRENCY(avgPayoutClosed),
                avgPaid: FORMAT.NUMBER_TO_CURRENCY(avgPayoutPaid),
                avgOpen: FORMAT.NUMBER_TO_CURRENCY(avgPayoutOpen),
                next30Cash: FORMAT.NUMBER_TO_CURRENCY(next30PayoutExposure),
                next30Accounts: next30EndingAccountsCount
            }
        };
    }, [
        statsAccounts,
        totalOpenAccounts,
        totalOpenEligibleAccounts,
        numEligibleProfitableOpen,
        numNotEligibleOpen,
        openProfitableOfAllOpenPercent,
        openProfitableOfEligibleOpenPercent,
        paidAccountsCount,
        closedAccountsCount,
        closedSurvivalRate,
        programCompleterPaidCount,
        programCompleterClosedCount,
        programSurvivalRate,
        totalPayoutPaid,
        totalPayoutPaidLast90Days,
        totalExpectedPayoutOpen,
        avgPayoutClosed,
        avgPayoutPaid,
        avgPayoutOpen,
        next30PayoutExposure,
        next30EndingAccountsCount
    ]);

    const handleJournalClick = (userId, accountType) => {
        axios
            .get(`/crud/users/${userId}/token`, {
                headers: { token: user?.token }
            })
            .then((response) => {
                const userToken = response.data.token;
                let company = 'Personal';
                if (accountType === 'PCT') company = 'Peak Capital Trading';
                else if (accountType === 'PTT') company = 'Bear Bull Traders';
                else if (accountType === 'SIMULATOR_TTD' || accountType === 'SIMULATOR_TTF')
                    company = 'Trading Terminal';
                setViewAs({ token: userToken, id: userId }, { token: user?.token, id: user?.id });
                setCompany(company);
                navigate('/');
            })
            .catch((error) => {
                // eslint-disable-next-line no-console -- Preserve diagnostic detail when user impersonation fails.
                console.error('Failed to fetch user token:', error);
                message.error('Failed to switch to user journal');
            });
    };

    const handleGlobalRiskInputChange = (key, value) => {
        setGlobalRiskControlValues((prev) => ({ ...prev, [key]: value }));
    };

    const handleGlobalRiskDateChange = (key, date) => {
        setGlobalRiskControlValues((prev) => ({
            ...prev,
            [key]: date ? date.toISOString() : ''
        }));
    };

    const handleGlobalRiskReset = () => {
        setGlobalRiskControls([]);
        setGlobalRiskControlValues({
            max_loss: '',
            total_loss: '',
            equity_limit: '',
            unwind_all: '',
            prevent_premarket: '',
            prevent_aftermarket: ''
        });
        setRemoveOverrides(false);
    };

    const handleGlobalRiskSave = () => {
        // Build riskConfig object from selected controls and their values
        const riskConfig = {};

        RISK_CONTROL_MAP.forEach((opt) => {
            if (globalRiskControls.includes(opt.value)) {
                if (opt.hasInput) {
                    // Only add if value is not empty
                    if (globalRiskControlValues[opt.value] !== '') {
                        if (opt.value === 'unwind_all') {
                            // For unwind_all, value is a date string
                            riskConfig[opt.backendKey] = {
                                [opt.enabledKey]: true,
                                [opt.valueKey]: globalRiskControlValues[opt.value]
                            };
                        } else {
                            riskConfig[opt.backendKey] = {
                                [opt.enabledKey]: true,
                                [opt.valueKey]: Number(globalRiskControlValues[opt.value])
                            };
                        }
                    }
                } else {
                    // For boolean controls
                    riskConfig[opt.backendKey] = true;
                }
            } else {
                // For controls with input, if not enabled, set enabled: false
                if (opt.hasInput) {
                    if (opt.value === 'unwind_all') {
                        riskConfig[opt.backendKey] = {
                            [opt.enabledKey]: false,
                            [opt.valueKey]: ''
                        };
                    } else {
                        riskConfig[opt.backendKey] = {
                            [opt.enabledKey]: false,
                            [opt.valueKey]: 0
                        };
                    }
                }
                if (
                    !opt.hasInput &&
                    (opt.backendKey === 'preventPremarketTrading' ||
                        opt.backendKey === 'preventAfterMarketTrading')
                ) {
                    riskConfig[opt.backendKey] = false;
                }
            }
        });

        // Only send if at least one control is enabled
        const anyEnabled = Object.values(riskConfig).some((v) => {
            if (typeof v === 'object') return v.enabled;
            return v === true;
        });
        if (!anyEnabled) {
            message.warning('No risk controls selected');
            return;
        }
        mutateApplyRiskConfig({
            token: user?.token,
            riskConfig,
            removeOverrides
        });
    };

    const handleSyncAllJournals = () => {
        // TODO: Implement sync logic
        message.success('All journals synced');
    };

    const handleClosePositionsForAll = () => {
        closeAllPositions()
            .then(() => {
                message.success('All open positions/orders are getting closed/canceled ...');
                setClosePositionsPopoverOpen(false);
            })
            .catch((err) => {
                console.error(err);
                message.error('Failed to schedule position closures');
            });
    };

    // --- CSV Download Handler ---
    const handleDownloadCSV = async () => {
        setIsDownloadingCSV(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_PHANTOM_BASE_URL}/funded-account/csv-report`,
                {
                    headers: { token: user?.token },
                    responseType: 'blob'
                }
            );

            // Extract filename from Content-Disposition header
            const disposition = res.headers['content-disposition'];
            let filename = 'report.csv';
            if (disposition && disposition.includes('filename=')) {
                const [, extractedFilename] = disposition.match(/filename="?([^"]+)"?/) || [];
                if (extractedFilename) {
                    filename = extractedFilename;
                }
            }

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success('CSV downloaded');
        } catch (err) {
            console.error(err);
            message.error('Failed to download CSV');
        } finally {
            setIsDownloadingCSV(false);
        }
    };

    const handleBulkWorkflowApply = async () => {
        if (!bulkWorkflowStatus) {
            message.warning('Select a workflow status first');
            return;
        }

        if (selectedAccounts.length === 0) {
            message.warning('Select at least one account');
            return;
        }

        setBulkActionLoading(true);
        try {
            const results = await Promise.allSettled(
                selectedAccounts.map((account) =>
                    updateAccountStatus(user?.token, account._id, bulkWorkflowStatus)
                )
            );
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const failureCount = results.length - successCount;

            if (successCount > 0) {
                message.success(
                    failureCount > 0
                        ? `Updated workflow for ${successCount} account(s). ${failureCount} failed.`
                        : `Updated workflow for ${successCount} account(s).`
                );
            } else {
                message.error('Failed to update selected accounts');
            }

            setSelectedRowKeys([]);
            setBulkWorkflowStatus(undefined);
            setBulkEndDateValue(null);
            refetch();
        } catch (error) {
            message.error('Failed to update selected accounts');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkReinstate = () => {
        if (selectedAccounts.length === 0) {
            message.warning('Select at least one account');
            return;
        }

        if (selectedReinstateAccounts.length === 0) {
            message.warning('Bulk reinstate only applies to inactive PCT/PTT accounts');
            return;
        }

        const skippedCount = selectedAccounts.length - selectedReinstateAccounts.length;

        Modal.confirm({
            title: 'Reinstate Selected Accounts?',
            content:
                skippedCount > 0
                    ? `${selectedReinstateAccounts.length} account(s) will be reinstated. ` +
                      `${skippedCount} selected account(s) will be skipped because only inactive ` +
                      'PCT/PTT accounts can be bulk reinstated.'
                    : `${selectedReinstateAccounts.length} account(s) will be reinstated using the OUT flow.`,
            onOk: async () => {
                setBulkActionLoading(true);
                try {
                    const results = await Promise.allSettled(
                        selectedReinstateAccounts.map((account) =>
                            activateFundedAccount({
                                token: user?.token,
                                accountId: account._id,
                                reinstateMode: 'mark_out'
                            })
                        )
                    );
                    const successCount = results.filter(
                        (result) => result.status === 'fulfilled'
                    ).length;
                    const failureCount = results.length - successCount;

                    if (successCount > 0) {
                        message.success(
                            failureCount > 0 || skippedCount > 0
                                ? `Reinstated ${successCount} account(s). ${failureCount + skippedCount} skipped/failed.`
                                : `Reinstated ${successCount} account(s).`
                        );
                    } else {
                        message.error('Failed to reinstate selected accounts');
                    }

                    setSelectedRowKeys([]);
                    setBulkEndDateValue(null);
                    refetch();
                } catch (error) {
                    message.error('Failed to reinstate selected accounts');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleBulkArchive = () => {
        if (selectedAccounts.length === 0) {
            message.warning('Select at least one account');
            return;
        }

        if (selectedArchivableAccounts.length === 0) {
            message.warning('Bulk archive only applies to inactive PCT/PTT accounts');
            return;
        }

        const skippedCount = selectedAccounts.length - selectedArchivableAccounts.length;

        Modal.confirm({
            title: 'Archive Selected Accounts?',
            content:
                skippedCount > 0
                    ? `${selectedArchivableAccounts.length} account(s) will be archived. ` +
                      `${skippedCount} selected account(s) will be skipped because only inactive ` +
                      'PCT/PTT accounts can be archived.'
                    : `${selectedArchivableAccounts.length} account(s) will be moved to Archived.`,
            onOk: async () => {
                setBulkActionLoading(true);
                try {
                    const results = await Promise.allSettled(
                        selectedArchivableAccounts.map((account) =>
                            archiveFundedAccount({
                                token: user?.token,
                                accountId: account._id
                            })
                        )
                    );
                    const successCount = results.filter(
                        (result) => result.status === 'fulfilled'
                    ).length;
                    const failureCount = results.length - successCount;

                    if (successCount > 0) {
                        message.success(
                            failureCount > 0 || skippedCount > 0
                                ? `Archived ${successCount} account(s). ${failureCount + skippedCount} skipped/failed.`
                                : `Archived ${successCount} account(s).`
                        );
                    } else {
                        message.error('Failed to archive selected accounts');
                    }

                    setSelectedRowKeys([]);
                    setBulkEndDateValue(null);
                    refetch();
                } catch (error) {
                    message.error('Failed to archive selected accounts');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleBulkEndDateApply = async () => {
        if (!bulkEndDateValue) {
            message.warning('Select an end date first');
            return;
        }

        if (selectedAccounts.length === 0) {
            message.warning('Select at least one account');
            return;
        }

        setBulkActionLoading(true);
        try {
            const results = await Promise.allSettled(
                selectedAccounts.map((account) =>
                    updateEndDate(user?.token, account._id, toNyEndOfDayISOString(bulkEndDateValue))
                )
            );
            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const failureCount = results.length - successCount;

            if (successCount > 0) {
                message.success(
                    failureCount > 0
                        ? `Updated end date for ${successCount} account(s). ${failureCount} failed.`
                        : `Updated end date for ${successCount} account(s).`
                );
            } else {
                message.error('Failed to update end date for selected accounts');
            }

            setSelectedRowKeys([]);
            setBulkEndDateValue(null);
            refetch();
        } catch (error) {
            message.error('Failed to update end date for selected accounts');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleTypeChange = (value) => {
        setAdminSelectedType(value);
        setPage(1);
        setSelectedRowKeys([]);
        setBulkWorkflowStatus(undefined);
        setBulkEndDateValue(null);
    };

    const handleReinstateAccount = (record) => {
        let shouldResetAccountData = false;

        Modal.confirm({
            title: 'Reinstate Account?',
            content: (
                <div>
                    <div style={{ marginBottom: record.type === 'ptt' ? 12 : 0 }}>
                        This will mark the account OUT, reactivate it, clear its review state, and
                        suppress future automated drawdown retriggers.
                    </div>
                    {record.type === 'ptt' && (
                        <Checkbox
                            onChange={(e) => {
                                shouldResetAccountData = e.target.checked;
                            }}
                        >
                            Clear account data (paid reset)
                        </Checkbox>
                    )}
                </div>
            ),
            onOk: async () => {
                mutateActivateAccount({
                    token: user?.token,
                    accountId: record._id,
                    reinstateMode:
                        record.type === 'ptt' && shouldResetAccountData ? 'reset' : 'mark_out'
                });
            }
        });
    };

    const handleRemoveOutStatus = (record) => {
        Modal.confirm({
            title: 'Remove OUT marker?',
            content:
                'This will remove the reinstated marker and allow future automated drawdown checks to trigger again on later uploads.',
            onOk: async () => {
                mutateDrawdownCheckStatus({
                    token: user?.token,
                    accountId: record._id,
                    drawdownCheckStatus: 'in'
                });
            }
        });
    };

    const hasDrawdownViolationReason = (record) =>
        Array.isArray(record?.riskViolationReason) &&
        record.riskViolationReason.some((reason) =>
            ['equityBelow9500', 'equityBelow10500'].includes(reason)
        );

    const isLegacyMarkedOut = (record) =>
        ['pct', 'ptt'].includes(record?.type) &&
        record?.isActive &&
        !record?.archived &&
        !record?.workflowStatus &&
        record?.riskViolation === true &&
        hasDrawdownViolationReason(record) &&
        !record?.drawdownCheckStatus;

    const rowSelection = {
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onChange: (keys) => setSelectedRowKeys(keys)
    };

    const columns = [
        {
            title: 'Account ID',
            dataIndex: 'accountUniqueId',
            key: 'accountUniqueId',
            sorter: (a, b) =>
                (a.accountUniqueId || '').localeCompare(b.accountUniqueId || '', undefined, {
                    numeric: true,
                    sensitivity: 'base'
                }),
            render: (accountUniqueId, record) => {
                const showOutMarker =
                    ['pct', 'ptt'].includes(record.type) &&
                    (record.drawdownCheckStatus === 'out' || isLegacyMarkedOut(record));

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <a
                            style={{ cursor: 'pointer' }}
                            href={`/admin/${record.accountUniqueId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {accountUniqueId}
                        </a>
                        {showOutMarker && (
                            <Popover
                                trigger="click"
                                placement="topLeft"
                                content={
                                    <div style={{ maxWidth: 260 }}>
                                        <div
                                            style={{
                                                marginBottom: 8,
                                                color: '#595959',
                                                lineHeight: 1.4
                                            }}
                                        >
                                            This account was reinstated using the OUT flow.
                                            Automated drawdown retriggers are currently suppressed.
                                        </div>
                                        <Button
                                            size="small"
                                            onClick={() => handleRemoveOutStatus(record)}
                                            loading={isUpdatingDrawdownStatus}
                                        >
                                            Remove OUT
                                        </Button>
                                    </div>
                                }
                            >
                                <span className="admin-dashboard-out-badge">
                                    <StopOutlined style={{ fontSize: 11 }} />
                                    OUT
                                </span>
                            </Popover>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'User Email',
            dataIndex: ['user', 'email'],
            key: 'userEmail',
            render: (_, record) =>
                record.user?.email ? (
                    //   <a
                    //       style={{ cursor: 'pointer' }}
                    //       onClick={() => navigate(`/admin/${record.accountUniqueId}`)}
                    // >
                    //       {record.user.email}
                    // </a>
                    <span>{record.user.email}</span>
                ) : (
                    '-'
                )
        },
        // Conditionally include optional columns
        ...optionalColumns
            .filter((col) => visibleColumns[col.key])
            .map((col) => ({
                title: col.title,
                dataIndex: col.dataIndex,
                key: col.key,
                render: col.render
            })),
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const status = getConsolidatedStatus(record);
                const workflowStatus = getWorkflowStatus(record);

                // Determine if account is Open, Inactive, or Archived
                const isOpen = record.isActive && !record.closedStatus;
                const isInactive = !record.isActive && !record.closedStatus;
                const isNotUnderReviewFlow =
                    workflowStatus !== 'needs_review' &&
                    workflowStatus !== 'needs_review_awaiting_info' &&
                    workflowStatus !== 'not_eligible' &&
                    workflowStatus !== 'reviewed_contact' &&
                    workflowStatus !== 'reviewed_contacted' &&
                    workflowStatus !== 'awaiting_payment' &&
                    workflowStatus !== 'paid';
                const isArchived = record.archived === true;

                // Show badge for Open, Inactive, or Archived accounts (regardless of tab)
                if (isNotUnderReviewFlow && (isOpen || isInactive || isArchived)) {
                    return (
                        <Tag color={status.tagColor} style={{ fontWeight: 600 }}>
                            {status.text}
                        </Tag>
                    );
                }

                // For closed accounts with workflow status, show dropdown
                // (This applies to workflow tabs and "All" tab)
                return (
                    <Select
                        className="admin-dashboard-select"
                        size="small"
                        value={workflowStatus}
                        style={{ width: 200 }}
                        onChange={(val) => handleStatusChange(record, val)}
                        loading={isUpdatingStatus}
                        placeholder="Select status"
                        options={WORKFLOW_STATUS_OPTIONS}
                    />
                );
            }
        },
        {
            title: 'Account Age',
            key: 'accountAge',
            filters: ACCOUNT_AGE_COLUMN_FILTERS,
            onFilter: (value, record) => matchesAccountAgeColumnFilter(record, value),
            sorter: (a, b) => (getAccountAgeDays(a) ?? -1) - (getAccountAgeDays(b) ?? -1),
            render: (_, record) => {
                const ageDays = getAccountAgeDays(record);
                if (ageDays == null) return '-';

                const activationDate = getAccountActivationDate(record);
                const label = `${ageDays} ${ageDays === 1 ? 'day' : 'days'}`;

                return activationDate ? (
                    <span
                        title={`Activated ${toUtcCalendarDate(activationDate).format('MMM D, YYYY')} UTC`}
                    >
                        {label}
                    </span>
                ) : (
                    label
                );
            }
        },
        {
            title: 'Payout Alerts',
            key: 'payoutAlerts',
            filters: getPayoutAlertColumnFilters(activeTab),
            onFilter: (value, record) =>
                matchesPayoutAlertColumnFilter(record, value, payoutAlertsByAccount),
            render: (_, record) => {
                const showProfile = ['simulator_ttf', 'pct', 'ptt'].includes(record.type);
                if (!showProfile) return '-';

                const badges = getPayoutAlertBadges(payoutAlertsByAccount[record.accountUniqueId], {
                    showBelowMinTradingDays: shouldShowBelowMinTradingDaysAlert(record)
                });
                if (!badges.length) return '-';

                return (
                    <div className="admin-dashboard-payout-alerts">
                        {badges.map((badge) => (
                            <Tooltip key={badge.key} title={badge.title}>
                                <Tag className="admin-dashboard-payout-alert-badge">
                                    <WarningOutlined style={{ marginRight: 3, fontSize: 10 }} />
                                    {badge.label}
                                </Tag>
                            </Tooltip>
                        ))}
                    </div>
                );
            }
        },
        {
            title: 'Payout Eligibility',
            key: 'payoutEligibility',
            render: (_, record) => {
                const isViolation = record?.riskViolation === true; // true => not eligible
                const currentValue = isViolation;
                const reasons = Array.isArray(record?.riskViolationReason)
                    ? record.riskViolationReason
                    : [];
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Select
                            className="admin-dashboard-select"
                            size="small"
                            value={currentValue}
                            style={{ width: 130, marginRight: 0 }}
                            onChange={(val) => {
                                if (val === currentValue) return;
                                confirmChangePayoutEligibility(record, val);
                            }}
                            loading={isUpdatingRisk}
                            options={[
                                {
                                    label: (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4
                                            }}
                                        >
                                            <CheckCircleTwoTone twoToneColor="#389e0d" />
                                            Eligible
                                        </span>
                                    ),
                                    value: false
                                },
                                {
                                    label: (
                                        <span
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4
                                            }}
                                        >
                                            <CloseCircleTwoTone twoToneColor="#d4380d" />
                                            Not Eligible
                                        </span>
                                    ),
                                    value: true
                                }
                            ]}
                        />
                        {isViolation &&
                            reasons.length > 0 &&
                            reasons.map((reason) => (
                                <Tag
                                    key={reason}
                                    style={{
                                        margin: 0,
                                        fontSize: 10,
                                        backgroundColor: '#fff1f0',
                                        color: '#d4380d',
                                        border: '1px solid #ffa39e',
                                        padding: '0 6px'
                                    }}
                                >
                                    {REASON_SHORT_LABELS[reason] || reason}
                                </Tag>
                            ))}
                        {isViolation && (
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    const existingReasons = record?.riskViolationReason || [];
                                    confirmChangePayoutEligibility(
                                        record,
                                        true,
                                        Array.isArray(existingReasons) ? existingReasons : []
                                    );
                                }}
                                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                title="Edit Reasons"
                            />
                        )}
                    </div>
                );
            },
            filters: [
                { text: 'Eligible', value: 'eligible' },
                { text: 'Not Eligible', value: 'notEligible' }
            ],
            onFilter: (value, record) => {
                const isViolation = record?.riskViolation === true;
                if (value === 'eligible') return !isViolation;
                if (value === 'notEligible') return isViolation;
                return true;
            }
        },
        ...(selectedType === 'SIMULATOR_TTF' && visibleColumns.fundedAccountFee
            ? [
                  {
                      title: 'Funded Account Fee',
                      key: 'fundedAccountFee',
                      render: (_, record) => {
                          if (isLoadingFundedAccountPurchaseStats) {
                              return <span style={{ color: '#bbb' }}>Loading...</span>;
                          }

                          if (isFundedAccountPurchaseStatsError) {
                              return <span style={{ color: '#d4380d' }}>Unavailable</span>;
                          }

                          const lastPayment = getLatestFundedAccountPayment(
                              fundedAccountPurchaseStats,
                              record
                          );
                          const percentOff =
                              lastPayment &&
                              lastPayment.discount &&
                              typeof lastPayment.discount.percentOff !== 'undefined'
                                  ? lastPayment.discount.percentOff
                                  : null;
                          let feeContent = <span style={{ color: '#bbb' }}>Not found</span>;

                          if (lastPayment && typeof lastPayment.amount === 'number') {
                              feeContent =
                                  lastPayment.amount === 0 ? (
                                      <span style={{ color: '#bbb' }}>Free</span>
                                  ) : (
                                      FORMAT.NUMBER_TO_CURRENCY(lastPayment.amount)
                                  );
                          }

                          return (
                              <div>
                                  {feeContent}
                                  {percentOff !== null && (
                                      <Tooltip
                                          placement="top"
                                          title={
                                              <>
                                                  <div style={{ fontSize: '11px' }}>
                                                      percent off: {percentOff}
                                                  </div>
                                                  <div style={{ fontSize: '11px' }}>
                                                      promotion code:{' '}
                                                      {lastPayment?.discount?.promotionCode ??
                                                          'N/A'}
                                                  </div>
                                                  <div style={{ fontSize: '11px' }}>
                                                      coupon name:{' '}
                                                      {lastPayment?.discount?.couponName ?? 'N/A'}
                                                  </div>
                                              </>
                                          }
                                      >
                                          <Text
                                              type="danger"
                                              style={{ fontSize: '12px', marginLeft: '10px' }}
                                          >
                                              (%{percentOff})
                                          </Text>
                                      </Tooltip>
                                  )}
                              </div>
                          );
                      },
                      sorter: (a, b) => {
                          if (!fundedAccountPurchaseStats?.users) return 0;

                          const getPaymentAmount = (rec) => {
                              const last = getLatestFundedAccountPayment(
                                  fundedAccountPurchaseStats,
                                  rec
                              );
                              return typeof last?.amount === 'number' ? last.amount : null;
                          };

                          const amountA = getPaymentAmount(a);
                          const amountB = getPaymentAmount(b);

                          if (amountA === null && amountB === null) return 0;
                          if (amountA === null) return 1;
                          if (amountB === null) return -1;

                          return amountA - amountB;
                      }
                  }
              ]
            : []),
        {
            title: 'Equity',
            key: 'equity',
            sorter: (a, b) => {
                const equityA = typeof a.equity === 'number' ? a.equity : 0;
                const equityB = typeof b.equity === 'number' ? b.equity : 0;
                return equityA - equityB;
            },
            render: (_, record) => {
                if (typeof record.equity === 'number') {
                    let color;
                    if (record.equity < record.initialFunds) {
                        color = '#d4380d'; // red
                    } else if (record.equity > record.initialFunds) {
                        color = '#389e0d'; // green
                    } else {
                        color = undefined; // default color (equal)
                    }
                    return (
                        <span style={{ fontWeight: 500, color }}>
                            {FORMAT.NUMBER_TO_CURRENCY(record.equity)}
                        </span>
                    );
                } // <-- FIXED BRACKET HERE
                return '-';
            }
        },
        {
            title: 'Realized P&L',
            key: 'pnl',
            sorter: (a, b) => {
                // Handle cases where equity or initialFunds might be undefined
                const pnlA =
                    typeof a.equity === 'number' && typeof a.initialFunds === 'number'
                        ? a.equity - a.initialFunds
                        : 0;
                const pnlB =
                    typeof b.equity === 'number' && typeof b.initialFunds === 'number'
                        ? b.equity - b.initialFunds
                        : 0;

                return pnlA - pnlB;
            },
            filters: [
                { text: 'Positive P&L', value: 'positive' },
                { text: 'Negative P&L', value: 'negative' },
                { text: 'Zero P&L', value: 'zero' }
            ],
            onFilter: (value, record) => {
                if (typeof record.equity === 'number' && typeof record.initialFunds === 'number') {
                    const pnl = record.equity - record.initialFunds;

                    switch (value) {
                        case 'positive':
                            return pnl > 0;
                        case 'negative':
                            return pnl < 0;
                        case 'zero':
                            return pnl === 0;
                        default:
                            return true;
                    }
                }
                // For records without valid P&L data, only show them when no filter is applied
                return value === 'zero';
            },
            render: (_, record) => {
                if (typeof record.equity === 'number' && typeof record.initialFunds === 'number') {
                    const pnl = record.equity - record.initialFunds;
                    let color;
                    if (pnl > 0) {
                        color = '#389e0d';
                    } else if (pnl < 0) {
                        color = '#d4380d';
                    } else {
                        color = undefined;
                    }
                    return (
                        <span style={{ color, fontWeight: 500 }}>
                            {pnl >= 0 ? '+' : ''}
                            {FORMAT.NUMBER_TO_CURRENCY(pnl)}
                        </span>
                    );
                }
                return '-';
            }
        },
        {
            title: 'Payout Exposure',
            key: 'payoutExposure',
            filters: PAYOUT_EXPOSURE_COLUMN_FILTERS,
            onFilter: (value, record) => getPayoutExposureFilterValue(record) === value,
            sorter: (a, b) => getPayoutExposureAmount(a) - getPayoutExposureAmount(b),
            render: (_, record) => {
                if (!hasPayoutExposureData(record)) return '-';

                const payoutExposure = getPayoutExposureAmount(record);

                return (
                    <span
                        style={{
                            fontWeight: 500,
                            color: payoutExposure > 0 ? '#389e0d' : undefined
                        }}
                    >
                        {FORMAT.NUMBER_TO_CURRENCY(payoutExposure)}
                    </span>
                );
            }
        },
        {
            title: '',
            key: 'actions',
            render: (_, record) => {
                const canMarkOut =
                    ['pct', 'ptt'].includes(record.type) && !record.isActive && !record.archived;
                const canArchive =
                    ['pct', 'ptt'].includes(record.type) && !record.isActive && !record.archived;
                const showProfile = ['simulator_ttf', 'pct', 'ptt'].includes(record.type);

                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 16,
                            minWidth: 260
                        }}
                    >
                        <Space size={12} style={{ fontSize: 13, lineHeight: 1.3 }}>
                            {showProfile && (
                                <a
                                    style={{
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        color: '#4074b4'
                                    }}
                                    href={`/admin/${record.accountUniqueId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Profile
                                </a>
                            )}
                            <a
                                style={{
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    color: '#4074b4'
                                }}
                                href={
                                    !record.user?.id
                                        ? `/admin/u/${record.accountUniqueId}`
                                        : undefined
                                }
                                target={!record.user?.id ? '_blank' : undefined}
                                rel={!record.user?.id ? 'noopener noreferrer' : undefined}
                                onClick={
                                    !record.user?.id
                                        ? undefined
                                        : () =>
                                              handleJournalClick(
                                                  record.user?.id,
                                                  record.type?.toUpperCase()
                                              )
                                }
                            >
                                Journal
                            </a>
                        </Space>
                        <Space size={8} style={{ fontSize: 13, lineHeight: 1.3 }}>
                            {canMarkOut && (
                                <Button
                                    size="small"
                                    type="primary"
                                    loading={isUpdatingActivation}
                                    onClick={() => handleReinstateAccount(record)}
                                >
                                    Reinstate
                                </Button>
                            )}
                            {canArchive && (
                                <a
                                    style={{
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        color: '#b42318'
                                    }}
                                    onClick={() => {
                                        Modal.confirm({
                                            title: 'Archive Account?',
                                            content:
                                                'This will move the account to Archived and remove it from the active review workflow.',
                                            onOk: async () => {
                                                mutateArchiveAccount({
                                                    token: user?.token,
                                                    accountId: record._id
                                                });
                                            }
                                        });
                                    }}
                                >
                                    Archive
                                </a>
                            )}
                        </Space>
                    </div>
                );
            }
        }
    ];

    return (
        <div
            className="admin-dashboard-page"
            style={{ padding: 24, width: '100%', maxWidth: '100%' }}
        >
            <div
                className="admin-dashboard-hero"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    width: '100%'
                }}
            >
                <div
                    className="admin-dashboard-heading-group"
                    style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                >
                    <div
                        style={{ display: 'flex', flexDirection: 'column', gap: 2, marginRight: 8 }}
                    >
                        <Text
                            className="admin-dashboard-title"
                            style={{ fontSize: 28, fontWeight: 700 }}
                        >
                            Admin Dashboard
                        </Text>
                        <Text
                            style={{
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: 0.8,
                                textTransform: 'uppercase',
                                color: '#2962FF'
                            }}
                        >
                            Viewing {selectedTypeMeta.titleLabel}
                        </Text>
                    </div>
                    <Select
                        className="admin-dashboard-select"
                        value={selectedType}
                        style={{ width: 220 }}
                        options={TYPE_OPTIONS}
                        onChange={handleTypeChange}
                    />
                </div>
                <div className="admin-dashboard-hero-actions" style={{ display: 'flex', gap: 8 }}>
                    {canResumeLastViewedAccount && (
                        <Button onClick={() => navigate(`/admin/${lastViewedFundedAccountId}`)}>
                            Last Account: {lastViewedFundedAccountId}
                        </Button>
                    )}
                    <Popover
                        trigger="click"
                        open={globalSettingsOpen}
                        onOpenChange={setGlobalSettingsOpen}
                        placement="bottomRight"
                        content={
                            <div style={{ minWidth: 320, maxWidth: 400 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                    Global Risk Control
                                </div>

                                <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
                                    {removeOverrides ? (
                                        <div>
                                            <b>
                                                <WarningOutlined
                                                    style={{ color: '#d4380d', marginRight: 6 }}
                                                />
                                                <span style={{ color: '#d4380d' }}>
                                                    <u>Warning:</u>
                                                    These settings will apply to <u>
                                                        all accounts
                                                    </u>{' '}
                                                    across all companies and{' '}
                                                    <u>
                                                        remove any individual account risk overrides
                                                    </u>
                                                    .
                                                    <br />
                                                    <span style={{ color: '#d4380d' }}>
                                                        All users will have the same risk profile.
                                                    </span>
                                                </span>
                                            </b>
                                        </div>
                                    ) : (
                                        <div>
                                            This is a global risk control. It will apply to all
                                            accounts unless an account has its own overrides.
                                        </div>
                                    )}
                                </div>
                                <div
                                    style={{
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        marginBottom: 8
                                    }}
                                >
                                    {isLoadingGlobalRiskConfig ? (
                                        <div style={{ color: '#888', padding: 8 }}>
                                            Loading global risk controls...
                                        </div>
                                    ) : (
                                        RISK_CONTROL_MAP.map((opt) => (
                                            <div
                                                key={opt.value}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '4px 12px'
                                                }}
                                            >
                                                <Checkbox
                                                    checked={globalRiskControls.includes(opt.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setGlobalRiskControls([
                                                                ...globalRiskControls,
                                                                opt.value
                                                            ]);
                                                        } else {
                                                            setGlobalRiskControls(
                                                                globalRiskControls.filter(
                                                                    (v) => v !== opt.value
                                                                )
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {opt.label}
                                                </Checkbox>
                                                {opt.hasInput &&
                                                    globalRiskControls.includes(opt.value) &&
                                                    (opt.value === 'unwind_all' ? (
                                                        <DatePicker
                                                            showTime
                                                            allowClear
                                                            style={{ marginLeft: 8, width: 180 }}
                                                            placeholder="Select date/time"
                                                            value={
                                                                globalRiskControlValues.unwind_all
                                                                    ? dayjs(
                                                                          globalRiskControlValues.unwind_all
                                                                      )
                                                                    : null
                                                            }
                                                            onChange={(date) =>
                                                                handleGlobalRiskDateChange(
                                                                    'unwind_all',
                                                                    date
                                                                )
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    ) : (
                                                        <Input
                                                            type="number"
                                                            size="small"
                                                            style={{ marginLeft: 8, width: 100 }}
                                                            placeholder="Enter value"
                                                            value={
                                                                globalRiskControlValues[opt.value]
                                                            }
                                                            onChange={(e) =>
                                                                handleGlobalRiskInputChange(
                                                                    opt.value,
                                                                    e.target.value
                                                                )
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    ))}
                                            </div>
                                        ))
                                    )}
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{
                                        marginBottom: 8,
                                        marginLeft: 0,
                                        marginRight: 0,
                                        background: '#fafafa',
                                        boxShadow: 'none',
                                        padding: 0
                                    }}
                                    bodyStyle={{
                                        padding: '8px 16px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Checkbox
                                        checked={removeOverrides}
                                        onChange={(e) => setRemoveOverrides(e.target.checked)}
                                    >
                                        Force this risk profile to all accounts (overwrites any
                                        individual account settings)
                                    </Checkbox>
                                </Card>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: 8
                                    }}
                                >
                                    <Button size="small" onClick={handleGlobalRiskReset}>
                                        Reset
                                    </Button>
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={handleGlobalRiskSave}
                                        loading={isApplyingRiskConfig}
                                    >
                                        Save Controls
                                    </Button>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                            </div>
                        }
                    >
                        <Button
                            icon={<SettingOutlined />}
                            style={{ marginLeft: 12 }}
                            title="Global Risk Control"
                        >
                            Global Risk Control
                        </Button>
                    </Popover>
                    {(selectedType === 'SIMULATOR_TTD' || selectedType === 'SIMULATOR_TTF') && (
                        <Button
                            icon={<SyncOutlined />}
                            type="default"
                            onClick={handleSyncAllJournals}
                        >
                            Sync Simulator Trades for All Users
                        </Button>
                    )}
                    <Popover
                        trigger="click"
                        open={closePositionsPopoverOpen}
                        onOpenChange={setClosePositionsPopoverOpen}
                        placement="bottomRight"
                        content={
                            <div style={{ minWidth: 220, maxWidth: 300 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                    Close Positions
                                </div>
                                <div style={{ color: '#888', fontSize: 13 }}>
                                    Closing positions will liquidate all open trades for all the
                                    accounts, also cancels any open orders.
                                    <br />
                                    <br />
                                    Use this feature with caution, as it may result in realized
                                    losses.
                                    <br />
                                    <br />
                                    Recommended for when the close position job did not run as
                                    expected.
                                </div>
                                <Button
                                    type="primary"
                                    danger
                                    style={{ marginTop: 12 }}
                                    onClick={handleClosePositionsForAll}
                                >
                                    Close All Positions
                                </Button>
                            </div>
                        }
                    >
                        <Button
                            icon={<CloseCircleOutlined />}
                            style={{ marginLeft: 0 }}
                            title="Close Positions"
                            danger
                            onClick={() => setClosePositionsPopoverOpen(true)}
                        >
                            Close Positions
                        </Button>
                    </Popover>
                </div>
            </div>

            <MarginRequirementsPanel token={user?.token} />

            {/* --- START: MODIFIED STATISTICS SECTION --- */}
            <Card
                className="admin-dashboard-stats-card"
                style={{ marginBottom: 16 }}
                bodyStyle={{ padding: 0 }}
            >
                <div
                    className={`admin-dashboard-stats-header${statsCollapsed ? ' admin-dashboard-stats-header--collapsed' : ''}`}
                >
                    <div className="admin-dashboard-stats-title-row">
                        <div className="admin-dashboard-stats-heading">
                            <button
                                type="button"
                                className="admin-dashboard-stats-collapse-btn"
                                onClick={() => setStatsCollapsed((prev) => !prev)}
                                aria-label={
                                    statsCollapsed ? 'Expand statistics' : 'Collapse statistics'
                                }
                                title={statsCollapsed ? 'Expand statistics' : 'Collapse statistics'}
                            >
                                {statsCollapsed ? <RightOutlined /> : <DownOutlined />}
                            </button>
                            <div>
                                <div
                                    className="admin-dashboard-section-label"
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        letterSpacing: 1,
                                        textAlign: 'left'
                                    }}
                                >
                                    Statistics
                                </div>
                                <Text className="admin-dashboard-stats-scope-label">
                                    {statsScopeLabel}
                                </Text>
                            </div>
                        </div>
                        {isTtfStatsType && (
                            <div className="admin-dashboard-stats-scope-control">
                                <Text className="admin-dashboard-stats-scope-control-label">
                                    Started after {PAID_ACCOUNT_START_CUTOFF_LABEL}
                                </Text>
                                <Switch
                                    checked={usePaidAccountStatsScope}
                                    onChange={setUsePaidAccountStatsScope}
                                    checkedChildren="After"
                                    unCheckedChildren="All"
                                    style={{ minWidth: 58 }}
                                />
                            </div>
                        )}
                    </div>
                    {!statsCollapsed && (
                        <>
                            <AdminStatsPanel
                                totalAccounts={totalAccounts}
                                barSegments={statsPanelData.barSegments}
                                accountBreakdownOpenRows={statsPanelData.accountBreakdownOpenRows}
                                accountBreakdownOpenTotal={statsPanelData.accountBreakdownOpenTotal}
                                accountBreakdownClosedRows={
                                    statsPanelData.accountBreakdownClosedRows
                                }
                                accountBreakdownClosedTotal={
                                    statsPanelData.accountBreakdownClosedTotal
                                }
                                uncategorizedCount={statsPanelData.uncategorizedCount}
                                profitability={statsPanelData.profitability}
                                payouts={statsPanelData.payouts}
                                onOpenGrossLiabilityChart={() => setGrossLiabilityChartOpen(true)}
                            />
                            <AdminTradingAnalytics
                                selectedFundedType={selectedType}
                                openAccountsInScope={openAccounts}
                                usePaidAccountStatsScope={usePaidAccountStatsScope}
                                isTtfStatsType={isTtfStatsType}
                            />
                        </>
                    )}
                </div>
            </Card>
            {/* --- END: MODIFIED STATISTICS SECTION --- */}

            {/* Workflow Tabs */}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key);
                    setPage(1); // Reset to first page when switching tabs
                }}
                items={tabItemsWithCounts}
                style={{ marginBottom: 16 }}
                className="admin-dashboard-tabs"
            />

            <Card className="admin-dashboard-bulk-card" size="small" style={{ marginBottom: 16 }}>
                <div className="admin-dashboard-bulk-card-inner">
                    <div className="admin-dashboard-bulk-card-left">
                        <Space size={12} wrap>
                            <Text style={{ fontWeight: 600 }}>
                                {selectedRowKeys.length} selected
                            </Text>
                            <Button
                                size="small"
                                onClick={() => {
                                    setSelectedRowKeys([]);
                                    setBulkWorkflowStatus(undefined);
                                    setBulkEndDateValue(null);
                                }}
                                disabled={selectedRowKeys.length === 0 || bulkActionLoading}
                            >
                                Clear Selection
                            </Button>
                        </Space>
                        <Space size={8} wrap>
                            <Select
                                className="admin-dashboard-select"
                                placeholder="Bulk workflow status"
                                value={bulkWorkflowStatus}
                                onChange={setBulkWorkflowStatus}
                                options={WORKFLOW_STATUS_OPTIONS}
                                style={{ width: 230 }}
                                disabled={bulkActionLoading}
                                allowClear
                            />
                            <Button
                                type="primary"
                                onClick={handleBulkWorkflowApply}
                                loading={bulkActionLoading}
                                disabled={selectedRowKeys.length === 0 || !bulkWorkflowStatus}
                            >
                                Apply Workflow
                            </Button>
                            <DatePicker
                                value={bulkEndDateValue}
                                onChange={setBulkEndDateValue}
                                allowClear
                                disabled={bulkActionLoading}
                                placeholder="Bulk end date"
                            />
                            <Button
                                onClick={handleBulkEndDateApply}
                                loading={bulkActionLoading}
                                disabled={selectedRowKeys.length === 0 || !bulkEndDateValue}
                            >
                                Apply End Date
                            </Button>
                            <Button
                                danger
                                onClick={handleBulkArchive}
                                loading={bulkActionLoading}
                                disabled={selectedArchivableAccounts.length === 0}
                            >
                                Archive Selected
                            </Button>
                            {selectedType !== 'SIMULATOR_TTF' && (
                                <Button
                                    onClick={handleBulkReinstate}
                                    loading={bulkActionLoading}
                                    disabled={selectedReinstateAccounts.length === 0}
                                >
                                    Reinstate Selected
                                </Button>
                            )}
                        </Space>
                    </div>
                    <div className="admin-dashboard-bulk-card-right">
                        <Popover
                            trigger="click"
                            placement="bottomRight"
                            content={
                                <div style={{ minWidth: 200, padding: 8 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                        Column Visibility
                                    </div>
                                    {optionalColumnToggles.map((column) => {
                                        const isTtfOnlyDisabled =
                                            column.ttfOnly && selectedType !== 'SIMULATOR_TTF';

                                        return (
                                            <div key={column.key} style={{ marginBottom: 8 }}>
                                                <Checkbox
                                                    checked={visibleColumns[column.key] || false}
                                                    disabled={isTtfOnlyDisabled}
                                                    onChange={(e) => {
                                                        setVisibleColumns((prev) => ({
                                                            ...prev,
                                                            [column.key]: e.target.checked
                                                        }));
                                                    }}
                                                >
                                                    {column.title}
                                                    {column.ttfOnly ? ' (TTF only)' : ''}
                                                </Checkbox>
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                        >
                            <Button icon={<SettingOutlined />}>Columns</Button>
                        </Popover>
                        <Input.Search
                            placeholder="Search"
                            allowClear
                            style={{ width: 280 }}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                        />
                        <Button
                            icon={<DownloadOutlined />}
                            title="Download report as CSV"
                            onClick={handleDownloadCSV}
                            loading={isDownloadingCSV}
                        >
                            Download Report
                        </Button>
                    </div>
                </div>
            </Card>

            <Card className="admin-dashboard-table-card" bodyStyle={{ padding: 0 }}>
                <Table
                    key={`${activeTab}-${selectedType}-${search}`}
                    className="admin-dashboard-table"
                    dataSource={filteredAccounts.map((acc) => ({ ...acc, key: acc._id }))}
                    columns={columns}
                    rowSelection={rowSelection}
                    loading={isLoading}
                    pagination={pagination}
                    size="middle"
                    bordered
                    onChange={(paginationTable, _filters, _sorter, extra) => {
                        const nextPage = extra?.action === 'filter' ? 1 : paginationTable.current;

                        setPage(nextPage);
                        setPageSize(paginationTable.pageSize);

                        if (Array.isArray(extra?.currentDataSource)) {
                            setTableFilteredCount(extra.currentDataSource.length);
                        }
                    }}
                />
            </Card>
            <Modal
                title={modalTitle}
                open={eligibilityModalOpen}
                onOk={handleEligibilityModalOk}
                onCancel={handleEligibilityModalCancel}
                okText="Confirm"
                cancelText="Cancel"
                okButtonProps={{
                    disabled: !eligible && (!selectedReasons || selectedReasons.length === 0)
                }}
            >
                <div style={{ marginBottom: 12 }}>
                    {eligible
                        ? 'This will clear risk violation for this account.'
                        : 'This will mark this account with a risk violation.'}
                </div>
                {!eligible && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                            Reasons <span style={{ color: '#d4380d' }}>*</span>:
                        </div>
                        <Checkbox.Group
                            options={REASON_OPTIONS}
                            value={selectedReasons}
                            onChange={(values) => {
                                setSelectedReasons(values);
                            }}
                        />
                    </div>
                )}
            </Modal>

            <AdminGrossLiabilityChart
                open={grossLiabilityChartOpen}
                onClose={() => setGrossLiabilityChartOpen(false)}
                selectedFundedType={selectedType}
                usePaidAccountStatsScope={usePaidAccountStatsScope}
                isTtfStatsType={isTtfStatsType}
            />
        </div>
    );
}
